"use client";

import axios from "axios";
import { useEffect, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { toast } from "react-hot-toast";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSearchParams } from "next/navigation";
import Link from "next/link";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

type PlanType = "basic" | "starter" | "all-in" | "bundle";

interface Plan {
  type: PlanType;
  name: string;
  price: number;
  originalPrice: number;
  description: string;
}

interface DiscountData {
  basicRemaining: number;
  starterRemaining: number;
  allInRemaining: number;
  bundleRemaining: number;
}

interface CouponData {
  code: string;
  valid: boolean;
  message?: string;
  discountType: "PERCENTAGE" | "FIXED";
  discountValue: number;
}

const plans: Plan[] = [
  {
    type: "basic",
    name: "Basic",
    price: 79,
    originalPrice: 250,
    description: "Single-user boilerplate for solo apps",
  },
  {
    type: "starter",
    name: "Starter",
    price: 149,
    originalPrice: 500,
    description: "P2P marketplace boilerplate",
  },
  {
    type: "all-in",
    name: "All-in",
    price: 229,
    originalPrice: 700,
    description: "P2P marketplace boilerplate",
  },
  {
    type: "bundle",
    name: "Bundle",
    price: 209,
    originalPrice: 999,
    description: "Single-user + P2P marketplace boilerplates",
  },
];

// Cupones públicos que se aplican automáticamente desde la URL
const PUBLIC_COUPONS: Record<string, {
  discountType: 'PERCENTAGE' | 'FIXED';
  discountValue: number;
  message: string;
}> = {
  'BFCM2025': {
    discountType: 'PERCENTAGE',
    discountValue: 10,
    message: '🔥 10% OFF Black Friday Deal applied!'
  },
  'BF2025': {
    discountType: 'PERCENTAGE',
    discountValue: 10,
    message: '🔥 10% OFF Black Friday Deal applied!'
  },
};

const PricingSection = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isStarterExpanded, setIsStarterExpanded] = useState(false);
  const [isBundleExpanded, setIsBundleExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slots, setSlots] = useState<DiscountData | null>(null);
  const searchParams = useSearchParams();
  const [referralCode, setReferralCode] = useState<string | undefined>(undefined);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<CouponData | null>(null);

  // Auto-aplicar cupón desde URL
  useEffect(() => {
    const ref = searchParams?.get("ref")?.toString();
    setReferralCode(ref);

    const couponFromUrl = searchParams?.get('coupon');
    if (couponFromUrl && !appliedCoupon) {
      const upperCode = couponFromUrl.toUpperCase();
      setCouponCode(upperCode);

      // Valida si es un cupón público conocido
      if (PUBLIC_COUPONS[upperCode]) {
        const coupon = PUBLIC_COUPONS[upperCode];
        setAppliedCoupon({
          code: upperCode,
          valid: true,
          message: coupon.message,
          discountType: coupon.discountType,
          discountValue: coupon.discountValue
        });
        toast.success(coupon.message, { duration: 4000 });
      } else {
        // Si no es público, valida con el backend
        handleApplyCouponAuto(upperCode);
      }
    }
  }, [searchParams]);

  const handlePurchase = async (plan: PlanType) => {
    try {
      setIsLoading(true);
      const stripe = await stripePromise;
      const payload: { plan: PlanType; couponCode?: string; referralCode?: string } = { plan };
      if (appliedCoupon?.valid && couponCode) {
        payload.couponCode = couponCode;
      }
      if (referralCode) {
        payload.referralCode = referralCode;
      }

      const response = await axios.post("/api/stripe/checkout", payload);
      const { sessionId } = response.data;
      await stripe!.redirectToCheckout({ sessionId });
    } catch (error: any) {
      console.error("[STRIPE_CHECKOUT_ERROR]", error);
      const errorMessage =
        error.response?.data?.error || "Failed to initiate checkout. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyCouponAuto = async (code: string) => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const response = await axios.post("/api/coupons/validate", { code });
      if (response.data.valid) {
        setAppliedCoupon({
          code,
          valid: true,
          message: response.data.message,
          discountType: response.data.discountType,
          discountValue: response.data.discountValue,
        });
        toast.success(response.data.message || 'Coupon applied!');
      }
    } catch (error: any) {
      console.log("Coupon validation failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error("Enter a coupon code");
      return;
    }

    const upperCode = couponCode.toUpperCase();

    // Primero verifica si es un cupón público
    if (PUBLIC_COUPONS[upperCode]) {
      const coupon = PUBLIC_COUPONS[upperCode];
      setAppliedCoupon({
        code: upperCode,
        valid: true,
        message: coupon.message,
        discountType: coupon.discountType,
        discountValue: coupon.discountValue
      });
      toast.success(coupon.message);
      return;
    }

    // Si no es público, valida en backend
    setLoading(true);
    try {
      const response = await axios.post("/api/coupons/validate", { code: upperCode });
      if (response.data.valid) {
        setAppliedCoupon({
          code: upperCode,
          valid: true,
          message: response.data.message,
          discountType: response.data.discountType,
          discountValue: response.data.discountValue,
        });
        toast.success(response.data.message || "Coupon applied successfully!");
      } else {
        setAppliedCoupon({
          code: upperCode,
          valid: false,
          message: response.data.error,
          discountType: "PERCENTAGE",
          discountValue: 0,
        });
        toast.error(response.data.error || "Invalid coupon");
      }
    } catch (error: any) {
      setAppliedCoupon({
        code: upperCode,
        valid: false,
        message: error.response?.data?.error || "Failed to validate coupon",
        discountType: "PERCENTAGE",
        discountValue: 0,
      });
      toast.error(error.response?.data?.error || "Failed to validate coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setAppliedCoupon(null);
    toast.success("Coupon removed");
  };

  useEffect(() => {
    const fetchSlots = async () => {
      try {
        const res = await fetch("/api/purchase/stats");
        const data: DiscountData = await res.json();
        setSlots(data);
      } catch (error) {
        console.error("[FETCH_SLOTS_ERROR]", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, []);

  const singleUserFeatures = [
    {
      category: "Core Boilerplate",
      details: ["Next.js boilerplate (App Router, TS)", "Prisma database schema with Postgres/Supabase"],
    },
    {
      category: "Authentication & Security",
      details: [
        "Register/login for Users (Email + Google OAuth)",
        "Forgot password, change email, email verification",
        "Forms with CAPTCHA protection",
      ],
    },
    {
      category: "Routes & Panels",
      details: [
        "Admin and User routes",
        "Admin panel for management",
        "Favorites for Listings (button, view, routes)",
      ],
    },
    {
      category: "UI & Functionality",
      details: [
        "Navbar and search listings (components + logic)",
        "Footer with newsletter setup",
        "Reviews calculations and components",
        "Extensive components (modals, buttons, headings, errors, maps)",
        "Responsive frontend pages (landing, etc.)",
        "Custom hooks for reusability",
      ],
    },
    {
      category: "Integrations",
      details: [
        "Contact form with emails via Resend",
        "Currency converter",
        "Premium subscription for Users (Stripe + invoice generation)",
      ],
    },
    {
      category: "Automation & SEO",
      details: [
        "Cron jobs for automated tasks",
        "Sitemap generator, robots.txt, and PSEO folder",
        "ChatGPT prompts for terms, privacy, data deletion",
      ],
    },
    {
      category: "Deployment",
      details: [
        "Dockerfile, .dockerignore, docker-compose for VPS/Coolify",
        "README with setup instructions",
      ],
    },
  ];

  const p2pFeatures = [
    {
      category: "P2P Marketplace",
      details: [
        "Buyer & Seller user types with dedicated routes",
        "Marketplace transaction logic (listings, offers, payments)",
        "Buyer/Seller dashboards for managing listings and transactions",
      ],
    },
    {
      category: "Advanced Routes",
      details: [
        "Dynamic Buyer/Seller profiles",
        "Secure messaging between users",
        "Dispute resolution components",
      ],
    },
    {
      category: "UI & Functionality",
      details: [
        "Marketplace-specific components (listing cards, offer modals)",
        "Real-time notifications for transactions",
        "Responsive P2P pages (marketplace, profiles, checkout)",
      ],
    },
  ];

  const formatPrice = (price: number) => {
    const dollars = Math.floor(price);
    const cents = Math.round((price - dollars) * 100);
    return (
      <>
        {dollars}
        <span className="text-lg align-top">.{cents.toString().padStart(2, "0")}</span>
      </>
    );
  };

  const getDisplayPrice = (plan: Plan): JSX.Element => {
    let displayElements: JSX.Element[] = [];

    if (appliedCoupon?.valid) {
      let discountedPrice = plan.price;
      const { discountType, discountValue } = appliedCoupon;

      if (discountType === "PERCENTAGE" && discountValue > 0) {
        discountedPrice = plan.price * (1 - discountValue / 100);
      } else if (discountType === "FIXED" && discountValue > 0) {
        discountedPrice = Math.max(0, plan.price - discountValue);
      }

      displayElements.push(
        <s key="original" className="text-xl text-gray-500 mr-2">
          ${formatPrice(plan.price)}
        </s>,
        <span key="discounted" className="text-cyan-500">
          ${formatPrice(discountedPrice)}
        </span>
      );
    } else {
      displayElements.push(<span key="normal">${formatPrice(plan.price)}</span>);
    }

    return <>{displayElements}</>;
  };

  return (
    <section id="pricing" className="bg-gray-900 py-20 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {appliedCoupon?.valid && (
          <div className="mb-6 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-center py-3 px-4 rounded-lg shadow-lg">
            <p className="font-bold text-lg">🔥 Black Friday Deal Active!</p>
            <p className="text-sm">
              {appliedCoupon.message} - Limited time offer
            </p>
          </div>
        )}
        
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Pricing Plans</h2>
        
        {/* Main Plans Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
          {/* Starter Plan */}
          <div className="bg-black p-8 rounded-lg shadow-md text-center flex flex-col min-h-[300px]">
            <h3 className="text-2xl font-semibold mb-4">{plans[1].name}</h3>
            <p className="text-5xl font-bold mb-2">{getDisplayPrice(plans[1])}</p>
            <p className="text-xl text-gray-400 line-through mb-4">${formatPrice(plans[1].originalPrice)}</p>
            <p className="text-sm text-gray-400 mb-4">{plans[1].description}</p>
            <ul className="text-left text-sm font-bold space-y-2 mb-4 flex-grow">
              {singleUserFeatures.map((feature, index) => (
                <li key={index}>
                  <span className="text-cyan-400 mr-2">✔</span>
                  {feature.category}
                  {isStarterExpanded && (
                    <ul className="ml-6 mt-1 space-y-1 text-gray-400">
                      {feature.details.map((detail, idx) => (
                        <li key={idx}>• {detail}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex flex-col">
              <button
                onClick={() => setIsStarterExpanded(!isStarterExpanded)}
                className="text-cyan-400 hover:underline mb-4 text-sm font-bold"
              >
                {isStarterExpanded ? (
                  <span className="flex items-center">
                    Show less <ChevronUp className="ml-1 h-4 w-4" />
                  </span>
                ) : (
                  <span className="flex items-center">
                    Show more <ChevronDown className="ml-1 h-4 w-4" />
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePurchase("starter")}
                disabled={isLoading}
                className={`inline-block bg-cyan-400 text-black font-semibold py-3 px-8 rounded-lg hover:bg-cyan-300 transition ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isLoading ? "Processing..." : "Buy Now"}
              </button>
              <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
                1 month of{" "}
                <Link
                  href="https://seozast.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:underline"
                >
                  SEOZast
                </Link>{" "}
                included!
              </p>
              <p className="mt-4 text-sm text-yellow-400 mb-2">
                {slots?.starterRemaining} discounted spots left
              </p>
            </div>
          </div>

          {/* All-in Plan */}
          <div className="bg-black p-8 rounded-lg shadow-md text-center flex flex-col min-h-[300px] border-2 border-cyan-400 relative">
            <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2">
              <span className="bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full shadow-md">
                Best Deal
              </span>
            </div>
            <h3 className="text-2xl font-semibold mb-4 mt-4">{plans[2].name}</h3>
            <p className="text-5xl font-bold mb-2">{getDisplayPrice(plans[2])}</p>
            <p className="text-xl text-gray-400 line-through mb-4">${formatPrice(plans[2].originalPrice)}</p>
            <p className="text-sm text-gray-400 mb-4">{plans[2].description}</p>
            <ul className="text-left text-sm font-bold space-y-2 mb-4 flex-grow">
              <li>
                <span className="text-cyan-400 mr-2">✔</span> All Starter features
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Lifetime updates
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Priority email support
              </li>
              <li className="text-cyan-400">
                <span className="text-yellow-400 mr-2">✔</span> Kickstart Session (1h setup call with me)
              </li>
              <li className="text-yellow-400 font-semibold">Smart choice for busy builders</li>
            </ul>
            <div className="flex flex-col">
              <div className="mb-4 h-[24px]" />
              <button
                onClick={() => handlePurchase("all-in")}
                disabled={isLoading}
                className={`inline-block bg-cyan-400 text-black font-semibold py-3 px-8 rounded-lg hover:bg-cyan-300 transition ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isLoading ? "Processing..." : "Buy Now"}
              </button>
              <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
                1 month of{" "}
                <Link
                  href="https://seozast.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:underline"
                >
                  SEOZast
                </Link>{" "}
                included!
              </p>
              <p className="mt-4 text-sm text-yellow-400 mb-2">
                {slots?.allInRemaining} discounted spots left
              </p>
            </div>
          </div>

          {/* Bundle Plan */}
          <div className="bg-black p-8 rounded-lg shadow-md text-center flex flex-col min-h-[300px] border-2 border-cyan-400 relative">
            <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2">
              <span className="bg-cyan-400 text-black text-xs font-bold px-3 py-1 rounded-full shadow-md">
                Ultimate Value
              </span>
            </div>
            <h3 className="text-2xl font-semibold mb-4 mt-4">{plans[3].name}</h3>
            <p className="text-5xl font-bold mb-2">{getDisplayPrice(plans[3])}</p>
            <p className="text-xl text-gray-400 line-through mb-4">${formatPrice(plans[3].originalPrice)}</p>
            <p className="text-sm text-gray-400 mb-4">{plans[3].description}</p>
            <ul className="text-left text-sm font-bold space-y-2 mb-4 flex-grow">
              <li>
                <span className="text-cyan-400 mr-2">✔</span> B2B boilerplate (like Peerquik)
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Dual-user P2P boilerplate (Buyers & Sellers)
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Lifetime updates
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Priority support (email)
              </li>
              {isBundleExpanded && (
                <>
                  {p2pFeatures.map((feature, index) => (
                    <li key={index}>
                      <span className="text-cyan-400 mr-2">✔</span>
                      {feature.category}
                      <ul className="ml-6 mt-1 space-y-1 text-gray-400">
                        {feature.details.map((detail, idx) => (
                          <li key={idx}>• {detail}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </>
              )}
              <li className="text-yellow-400 font-semibold">Perfect for solo apps & marketplaces</li>
            </ul>
            <div className="flex flex-col">
              <button
                onClick={() => setIsBundleExpanded(!isBundleExpanded)}
                className="text-cyan-400 hover:underline mb-4 text-sm font-bold"
              >
                {isBundleExpanded ? (
                  <span className="flex items-center">
                    Show less <ChevronUp className="ml-1 h-4 w-4" />
                  </span>
                ) : (
                  <span className="flex items-center">
                    Show more <ChevronDown className="ml-1 h-4 w-4" />
                  </span>
                )}
              </button>
              <button
                onClick={() => handlePurchase("bundle")}
                disabled={isLoading}
                className={`inline-block bg-cyan-400 text-black font-semibold py-3 px-8 rounded-lg hover:bg-cyan-300 transition ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isLoading ? "Processing..." : "Buy Now"}
              </button>
              <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
                1 month of{" "}
                <Link
                  href="https://seozast.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-400 hover:underline"
                >
                  SEOZast
                </Link>{" "}
                included!
              </p>
              <p className="mt-4 text-sm text-yellow-400 mb-2">
                {slots?.bundleRemaining} discounted spots left
              </p>
            </div>
          </div>
        </div>

        {/* Coupon Input */}
        <div className="mt-8">
          <p className="sm:text-lg text-md text-center mb-4 font-semibold">Do you have a coupon?</p>
          <div className="max-w-md mx-auto mb-8 flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              className="flex-grow px-4 py-2 border text-black border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-600"
              disabled={loading}
            />
            {appliedCoupon?.valid ? (
              <button
                onClick={handleRemoveCoupon}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                disabled={loading}
              >
                Remove
              </button>
            ) : (
              <button
                onClick={handleApplyCoupon}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                disabled={loading}
              >
                Apply Coupon
              </button>
            )}
          </div>
        </div>

        {appliedCoupon && (
          <div className="text-center mb-4 text-sm">
            {appliedCoupon.valid ? (
              <span className="text-cyan-600 font-medium">
                {appliedCoupon.message || `${appliedCoupon.code} applied!`}
              </span>
            ) : (
              <span className="text-red-600">{appliedCoupon.message || "Invalid coupon"}</span>
            )}
          </div>
        )}

        {/* Basic Plan Section */}
        <div className="max-w-3xl mx-auto mt-12">
          <h3 className="text-xl md:text-2xl font-semibold text-center mb-6">
            Need a simple single-user Next.js boilerplate for B2B?
          </h3>
          <div className="bg-black p-6 rounded-lg shadow-md text-center flex flex-col">
            <h4 className="text-xl font-semibold mb-4">{plans[0].name}</h4>
            <p className="text-4xl font-bold mb-2">{getDisplayPrice(plans[0])}</p>
            <p className="text-lg text-gray-400 line-through mb-4">${formatPrice(plans[0].originalPrice)}</p>
            <p className="text-sm text-gray-400 mb-4">{plans[0].description}</p>
            <ul className="text-left text-sm font-bold space-y-2 mb-4">
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Single-user boilerplate (like Peerquik)
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Core Next.js Boilerplate
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Authentication & Security
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Routes & Panels
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> UI & Functionality
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Stripe and other integrations
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Automation & SEO
              </li>
              <li>
                <span className="text-cyan-400 mr-2">✔</span> Deployment Vercel/VPS
              </li>
              <li className="text-yellow-400 font-semibold">Ideal for small B2B startups</li>
            </ul>
            <button
              onClick={() => handlePurchase("basic")}
              disabled={isLoading}
              className={`inline-block bg-cyan-400 text-black font-semibold py-2 px-6 rounded-lg hover:bg-cyan-300 transition ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? "Processing..." : "Buy Now"}
            </button>
            <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
              1 month of{" "}
              <Link
                href="https://seozast.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:underline"
              >
                SEOZast
              </Link>{" "}
              included!
            </p>
            <p className="mt-4 text-sm text-yellow-400">
              {slots?.basicRemaining} discounted spots left
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;

// "use client";

// import axios from "axios";
// import { useEffect, useState } from "react";
// import { loadStripe, Stripe } from "@stripe/stripe-js";
// import { toast } from "react-hot-toast";
// import { ChevronDown, ChevronUp } from "lucide-react";
// import { useSearchParams } from "next/navigation";
// import Link from "next/link";

// const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

// type PlanType = "basic" | "starter" | "all-in" | "bundle";

// interface Plan {
//   type: PlanType;
//   name: string;
//   price: number;
//   originalPrice: number;
//   description: string;
// }

// interface DiscountData {
//   basicRemaining: number;
//   starterRemaining: number;
//   allInRemaining: number;
//   bundleRemaining: number;
// }

// interface CouponData {
//   code: string;
//   valid: boolean;
//   message?: string;
//   discountType: "PERCENTAGE" | "FIXED";
//   discountValue: number;
// }

// const plans: Plan[] = [
//   {
//     type: "basic",
//     name: "Basic",
//     price: 79,
//     originalPrice: 250,
//     description: "Single-user boilerplate for solo apps",
//   },
//   {
//     type: "starter",
//     name: "Starter",
//     price: 149,
//     originalPrice: 500,
//     description: "P2P marketplace boilerplate",
//   },
//   {
//     type: "all-in",
//     name: "All-in",
//     price: 229,
//     originalPrice: 700,
//     description: "P2P marketplace boilerplate",
//   },
//   {
//     type: "bundle",
//     name: "Bundle",
//     price: 209,
//     originalPrice: 999,
//     description: "Single-user + P2P marketplace boilerplates",
//   },
// ];

// const PricingSection = () => {
//   const [isLoading, setIsLoading] = useState(false);
//   const [isStarterExpanded, setIsStarterExpanded] = useState(false);
//   const [isBundleExpanded, setIsBundleExpanded] = useState(false);
//   const [loading, setLoading] = useState(true);
//   const [slots, setSlots] = useState<DiscountData | null>(null);
//   const searchParams = useSearchParams();
//   const [referralCode, setReferralCode] = useState<string | undefined>(undefined);
//   const [couponCode, setCouponCode] = useState("");
//   const [appliedCoupon, setAppliedCoupon] = useState<CouponData | null>(null);

//   useEffect(() => {
//     const ref = searchParams?.get("ref")?.toString();
//     setReferralCode(ref);
//   }, [searchParams]);

//   const handlePurchase = async (plan: PlanType) => {
//     try {
//       setIsLoading(true);
//       const stripe = await stripePromise;
//       const payload: { plan: PlanType; couponCode?: string; referralCode?: string } = { plan };
//       if (appliedCoupon?.valid && couponCode) {
//         payload.couponCode = couponCode;
//       }
//       if (referralCode) {
//         payload.referralCode = referralCode;
//       }

//       const response = await axios.post("/api/stripe/checkout", payload);
//       const { sessionId } = response.data;
//       await stripe!.redirectToCheckout({ sessionId });
//     } catch (error: any) {
//       console.error("[STRIPE_CHECKOUT_ERROR]", error);
//       const errorMessage =
//         error.response?.data?.error || "Failed to initiate checkout. Please try again.";
//       toast.error(errorMessage);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleApplyCoupon = async () => {
//     if (!couponCode.trim()) {
//       toast.error("Enter a coupon code");
//       return;
//     }

//     setLoading(true);
//     try {
//       const response = await axios.post("/api/coupons/validate", { code: couponCode });
//       if (response.data.valid) {
//         setAppliedCoupon({
//           code: couponCode,
//           valid: true,
//           message: response.data.message,
//           discountType: response.data.discountType,
//           discountValue: response.data.discountValue,
//         });
//         toast.success(response.data.message || "Coupon applied successfully!");
//       } else {
//         setAppliedCoupon({
//           code: couponCode,
//           valid: false,
//           message: response.data.error,
//           discountType: "PERCENTAGE",
//           discountValue: 0,
//         });
//         toast.error(response.data.error || "Invalid coupon");
//       }
//     } catch (error: any) {
//       setAppliedCoupon({
//         code: couponCode,
//         valid: false,
//         message: error.response?.data?.error || "Failed to validate coupon",
//         discountType: "PERCENTAGE",
//         discountValue: 0,
//       });
//       toast.error(error.response?.data?.error || "Failed to validate coupon");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleRemoveCoupon = () => {
//     setCouponCode("");
//     setAppliedCoupon(null);
//     toast.success("Coupon removed");
//   };

//   useEffect(() => {
//     const fetchSlots = async () => {
//       try {
//         const res = await fetch("/api/purchase/stats");
//         const data: DiscountData = await res.json();
//         setSlots(data);
//       } catch (error) {
//         console.error("[FETCH_SLOTS_ERROR]", error);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchSlots();
//   }, []);

//   const singleUserFeatures = [
//     {
//       category: "Core Boilerplate",
//       details: ["Next.js boilerplate (App Router, TS)", "Prisma database schema with Postgres/Supabase"],
//     },
//     {
//       category: "Authentication & Security",
//       details: [
//         "Register/login for Users (Email + Google OAuth)",
//         "Forgot password, change email, email verification",
//         "Forms with CAPTCHA protection",
//       ],
//     },
//     {
//       category: "Routes & Panels",
//       details: [
//         "Admin and User routes",
//         "Admin panel for management",
//         "Favorites for Listings (button, view, routes)",
//       ],
//     },
//     {
//       category: "UI & Functionality",
//       details: [
//         "Navbar and search listings (components + logic)",
//         "Footer with newsletter setup",
//         "Reviews calculations and components",
//         "Extensive components (modals, buttons, headings, errors, maps)",
//         "Responsive frontend pages (landing, etc.)",
//         "Custom hooks for reusability",
//       ],
//     },
//     {
//       category: "Integrations",
//       details: [
//         "Contact form with emails via Resend",
//         "Currency converter",
//         "Premium subscription for Users (Stripe + invoice generation)",
//       ],
//     },
//     {
//       category: "Automation & SEO",
//       details: [
//         "Cron jobs for automated tasks",
//         "Sitemap generator, robots.txt, and PSEO folder",
//         "ChatGPT prompts for terms, privacy, data deletion",
//       ],
//     },
//     {
//       category: "Deployment",
//       details: [
//         "Dockerfile, .dockerignore, docker-compose for VPS/Coolify",
//         "README with setup instructions",
//       ],
//     },
//   ];

//   const p2pFeatures = [
//     {
//       category: "P2P Marketplace",
//       details: [
//         "Buyer & Seller user types with dedicated routes",
//         "Marketplace transaction logic (listings, offers, payments)",
//         "Buyer/Seller dashboards for managing listings and transactions",
//       ],
//     },
//     {
//       category: "Advanced Routes",
//       details: [
//         "Dynamic Buyer/Seller profiles",
//         "Secure messaging between users",
//         "Dispute resolution components",
//       ],
//     },
//     {
//       category: "UI & Functionality",
//       details: [
//         "Marketplace-specific components (listing cards, offer modals)",
//         "Real-time notifications for transactions",
//         "Responsive P2P pages (marketplace, profiles, checkout)",
//       ],
//     },
//   ];

//   const formatPrice = (price: number) => {
//     const dollars = Math.floor(price);
//     const cents = Math.round((price - dollars) * 100);
//     return (
//       <>
//         {dollars}
//         <span className="text-lg align-top">.{cents.toString().padStart(2, "0")}</span>
//       </>
//     );
//   };

//   const getDisplayPrice = (plan: Plan): JSX.Element => {
//     let displayElements: JSX.Element[] = [];

//     if (appliedCoupon?.valid) {
//       let discountedPrice = plan.price;
//       const { discountType, discountValue } = appliedCoupon;

//       if (discountType === "PERCENTAGE" && discountValue > 0) {
//         discountedPrice = plan.price * (1 - discountValue / 100);
//       } else if (discountType === "FIXED" && discountValue > 0) {
//         discountedPrice = Math.max(0, plan.price - discountValue);
//       }

//       displayElements.push(
//         <s key="original" className="text-xl text-gray-500 mr-2">
//           ${formatPrice(plan.price)}
//         </s>,
//         <span key="discounted" className="text-cyan-500">
//           ${formatPrice(discountedPrice)}
//         </span>
//       );
//     } else {
//       displayElements.push(<span key="normal">${formatPrice(plan.price)}</span>);
//     }

//     return <>{displayElements}</>;
//   };

//   return (
//     <section id="pricing" className="bg-gray-900 py-20 text-white">
//       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
//         <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Pricing Plans</h2>
//         {/* Main Plans Grid */}
//         <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12">
//           {/* Starter Plan */}
//           <div className="bg-black p-8 rounded-lg shadow-md text-center flex flex-col min-h-[300px]">
//             <h3 className="text-2xl font-semibold mb-4">{plans[1].name}</h3>
//             <p className="text-5xl font-bold mb-2">{getDisplayPrice(plans[1])}</p>
//             <p className="text-xl text-gray-400 line-through mb-4">${formatPrice(plans[1].originalPrice)}</p>
//             <p className="text-sm text-gray-400 mb-4">{plans[1].description}</p>
//             <ul className="text-left text-sm font-bold space-y-2 mb-4 flex-grow">
//               {singleUserFeatures.map((feature, index) => (
//                 <li key={index}>
//                   <span className="text-cyan-400 mr-2">✔</span>
//                   {feature.category}
//                   {isStarterExpanded && (
//                     <ul className="ml-6 mt-1 space-y-1 text-gray-400">
//                       {feature.details.map((detail, idx) => (
//                         <li key={idx}>• {detail}</li>
//                       ))}
//                     </ul>
//                   )}
//                 </li>
//               ))}
//             </ul>
//             <div className="flex flex-col">
//               <button
//                 onClick={() => setIsStarterExpanded(!isStarterExpanded)}
//                 className="text-cyan-400 hover:underline mb-4 text-sm font-bold"
//               >
//                 {isStarterExpanded ? (
//                   <span className="flex items-center">
//                     Show less <ChevronUp className="ml-1 h-4 w-4" />
//                   </span>
//                 ) : (
//                   <span className="flex items-center">
//                     Show more <ChevronDown className="ml-1 h-4 w-4" />
//                   </span>
//                 )}
//               </button>
//               <button
//                 onClick={() => handlePurchase("starter")}
//                 disabled={isLoading}
//                 className={`inline-block bg-cyan-400 text-black font-semibold py-3 px-8 rounded-lg hover:bg-cyan-300 transition ${
//                   isLoading ? "opacity-50 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isLoading ? "Processing..." : "Buy Now"}
//               </button>
//               <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
//                 1 month of{" "}
//                 <Link
//                   href="https://seozast.com"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-emerald-400 hover:underline"
//                 >
//                   SEOZast
//                 </Link>{" "}
//                 included!
//               </p>
//               <p className="mt-4 text-sm text-yellow-400 mb-2">
//                 {slots?.starterRemaining} discounted spots left
//               </p>
//             </div>
//           </div>

//           {/* All-in Plan */}
//           <div className="bg-black p-8 rounded-lg shadow-md text-center flex flex-col min-h-[300px] border-2 border-cyan-400 relative">
//             <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2">
//               <span className="bg-yellow-400 text-black text-xs font-bold px-3 py-1 rounded-full shadow-md">
//                 Best Deal
//               </span>
//             </div>
//             <h3 className="text-2xl font-semibold mb-4 mt-4">{plans[2].name}</h3>
//             <p className="text-5xl font-bold mb-2">{getDisplayPrice(plans[2])}</p>
//             <p className="text-xl text-gray-400 line-through mb-4">${formatPrice(plans[2].originalPrice)}</p>
//             <p className="text-sm text-gray-400 mb-4">{plans[2].description}</p>
//             <ul className="text-left text-sm font-bold space-y-2 mb-4 flex-grow">
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> All Starter features
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Lifetime updates
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Priority email support
//               </li>
//               <li className="text-cyan-400">
//                 <span className="text-yellow-400 mr-2">✔</span> Kickstart Session (1h setup call with me)
//               </li>
//               <li className="text-yellow-400 font-semibold">Smart choice for busy builders</li>
//             </ul>
//             <div className="flex flex-col">
//               <div className="mb-4 h-[24px]" />
//               <button
//                 onClick={() => handlePurchase("all-in")}
//                 disabled={isLoading}
//                 className={`inline-block bg-cyan-400 text-black font-semibold py-3 px-8 rounded-lg hover:bg-cyan-300 transition ${
//                   isLoading ? "opacity-50 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isLoading ? "Processing..." : "Buy Now"}
//               </button>
//               <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
//                 1 month of{" "}
//                 <Link
//                   href="https://seozast.com"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-emerald-400 hover:underline"
//                 >
//                   SEOZast
//                 </Link>{" "}
//                 included!
//               </p>
//               <p className="mt-4 text-sm text-yellow-400 mb-2">
//                 {slots?.allInRemaining} discounted spots left
//               </p>
//             </div>
//           </div>

//           {/* Bundle Plan */}
//           <div className="bg-black p-8 rounded-lg shadow-md text-center flex flex-col min-h-[300px] border-2 border-cyan-400 relative">
//             <div className="absolute top-0 left-0 right-0 flex justify-center -translate-y-1/2">
//               <span className="bg-cyan-400 text-black text-xs font-bold px-3 py-1 rounded-full shadow-md">
//                 Ultimate Value
//               </span>
//             </div>
//             <h3 className="text-2xl font-semibold mb-4 mt-4">{plans[3].name}</h3>
//             <p className="text-5xl font-bold mb-2">{getDisplayPrice(plans[3])}</p>
//             <p className="text-xl text-gray-400 line-through mb-4">${formatPrice(plans[3].originalPrice)}</p>
//             <p className="text-sm text-gray-400 mb-4">{plans[3].description}</p>
//             <ul className="text-left text-sm font-bold space-y-2 mb-4 flex-grow">
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> B2B boilerplate (like Peerquik)
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Dual-user P2P boilerplate (Buyers & Sellers)
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Lifetime updates
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Priority support (email)
//               </li>
//               {isBundleExpanded && (
//                 <>
//                   {p2pFeatures.map((feature, index) => (
//                     <li key={index}>
//                       <span className="text-cyan-400 mr-2">✔</span>
//                       {feature.category}
//                       <ul className="ml-6 mt-1 space-y-1 text-gray-400">
//                         {feature.details.map((detail, idx) => (
//                           <li key={idx}>• {detail}</li>
//                         ))}
//                       </ul>
//                     </li>
//                   ))}
//                 </>
//               )}
//               <li className="text-yellow-400 font-semibold">Perfect for solo apps & marketplaces</li>
//             </ul>
//             <div className="flex flex-col">
//               <button
//                 onClick={() => setIsBundleExpanded(!isBundleExpanded)}
//                 className="text-cyan-400 hover:underline mb-4 text-sm font-bold"
//               >
//                 {isBundleExpanded ? (
//                   <span className="flex items-center">
//                     Show less <ChevronUp className="ml-1 h-4 w-4" />
//                   </span>
//                 ) : (
//                   <span className="flex items-center">
//                     Show more <ChevronDown className="ml-1 h-4 w-4" />
//                   </span>
//                 )}
//               </button>
//               <button
//                 onClick={() => handlePurchase("bundle")}
//                 disabled={isLoading}
//                 className={`inline-block bg-cyan-400 text-black font-semibold py-3 px-8 rounded-lg hover:bg-cyan-300 transition ${
//                   isLoading ? "opacity-50 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isLoading ? "Processing..." : "Buy Now"}
//               </button>
//               <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
//                 1 month of{" "}
//                 <Link
//                   href="https://seozast.com"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-emerald-400 hover:underline"
//                 >
//                   SEOZast
//                 </Link>{" "}
//                 included!
//               </p>
//               <p className="mt-4 text-sm text-yellow-400 mb-2">
//                 {slots?.bundleRemaining} discounted spots left
//               </p>
//             </div>
//           </div>
//         </div>

//         {/* Coupon Input */}
//         <div className="mt-8">
//           <p className="sm:text-lg text-md text-center mb-4 font-semibold">Do you have a coupon?</p>
//           <div className="max-w-md mx-auto mb-8 flex flex-col sm:flex-row gap-2">
//             <input
//               type="text"
//               value={couponCode}
//               onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
//               placeholder="Enter coupon code"
//               className="flex-grow px-4 py-2 border text-black border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-600"
//               disabled={loading}
//             />
//             {appliedCoupon?.valid ? (
//               <button
//                 onClick={handleRemoveCoupon}
//                 className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
//                 disabled={loading}
//               >
//                 Remove
//               </button>
//             ) : (
//               <button
//                 onClick={handleApplyCoupon}
//                 className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
//                 disabled={loading}
//               >
//                 Apply Coupon
//               </button>
//             )}
//           </div>
//         </div>

//         {appliedCoupon && (
//           <div className="text-center mb-4 text-sm">
//             {appliedCoupon.valid ? (
//               <span className="text-cyan-600 font-medium">
//                 {appliedCoupon.message || `${appliedCoupon.code} applied!`}
//               </span>
//             ) : (
//               <span className="text-red-600">{appliedCoupon.message || "Invalid coupon"}</span>
//             )}
//           </div>
//         )}

//         {/* Basic Plan Section */}
//         <div className="max-w-3xl mx-auto mt-12">
//           <h3 className="text-xl md:text-2xl font-semibold text-center mb-6">
//             Need a simple single-user Next.js boilerplate for B2B?
//           </h3>
//           <div className="bg-black p-6 rounded-lg shadow-md text-center flex flex-col">
//             <h4 className="text-xl font-semibold mb-4">{plans[0].name}</h4>
//             <p className="text-4xl font-bold mb-2">{getDisplayPrice(plans[0])}</p>
//             <p className="text-lg text-gray-400 line-through mb-4">${formatPrice(plans[0].originalPrice)}</p>
//             <p className="text-sm text-gray-400 mb-4">{plans[0].description}</p>
//             <ul className="text-left text-sm font-bold space-y-2 mb-4">
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Single-user boilerplate (like Peerquik)
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Core Next.js Boilerplate
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Authentication & Security
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Routes & Panels
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> UI & Functionality
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Stripe and other integrations
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Automation & SEO
//               </li>
//               <li>
//                 <span className="text-cyan-400 mr-2">✔</span> Deployment Vercel/VPS
//               </li>
//               <li className="text-yellow-400 font-semibold">Ideal for small B2B startups</li>
//             </ul>
//             <button
//               onClick={() => handlePurchase("basic")}
//               disabled={isLoading}
//               className={`inline-block bg-cyan-400 text-black font-semibold py-2 px-6 rounded-lg hover:bg-cyan-300 transition ${
//                 isLoading ? "opacity-50 cursor-not-allowed" : ""
//               }`}
//             >
//               {isLoading ? "Processing..." : "Buy Now"}
//             </button>
//             <p className="mt-3 text-white font-semibold text-sm px-3 py-2 rounded-md text-center">
//               1 month of{" "}
//               <Link
//                 href="https://seozast.com"
//                 target="_blank"
//                 rel="noopener noreferrer"
//                 className="text-emerald-400 hover:underline"
//               >
//                 SEOZast
//               </Link>{" "}
//               included!
//             </p>
//             <p className="mt-4 text-sm text-yellow-400">
//               {slots?.basicRemaining} discounted spots left
//             </p>
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// export default PricingSection;